import React, { useEffect, useState } from 'react';
import langeImg from '@/assets/quotes/lange.jpg';
import poundImg from '@/assets/quotes/pound.jpg';
import jobsImg from '@/assets/quotes/jobs.jpg';
import lessigImg from '@/assets/quotes/lessig.jpg';
import turingImg from '@/assets/quotes/turing.jpg';
import lovelaceImg from '@/assets/quotes/lovelace.jpg';
import russellImg from '@/assets/quotes/russell.jpg';
import gebruImg from '@/assets/quotes/gebru.jpg';
import holmesImg from '@/assets/quotes/holmes.jpg';
import thembekwayoImg from '@/assets/quotes/thembekwayo.jpg';

type Entry = {
  author: string;
  title: string;
  label: string;
  image: string;
  quote: string;
};

const entries: Entry[] = [
  {
    author: 'Christian Lous Lange',
    title: 'AI Governance',
    label: 'Ethical Frameworks',
    image: langeImg,
    quote: 'Technology is a useful servant but a dangerous master.',
  },
  {
    author: 'Stuart Russell',
    title: 'Human-Compatible AI',
    label: 'Alignment & Safety',
    image: russellImg,
    quote:
      'The real risk with AI isn\u2019t malice but competence \u2014 a superintelligent AI will be extremely good at accomplishing its goals, and if those goals aren\u2019t aligned with ours, we\u2019re in trouble.',
  },
  {
    author: 'Timnit Gebru',
    title: 'Algorithmic Accountability',
    label: 'Bias & Fairness',
    image: gebruImg,
    quote:
      'You can\u2019t have ethical AI that isn\u2019t inclusive. And whoever is creating the technology is setting the standards.',
  },
  {
    author: 'Roscoe Pound',
    title: 'Adaptive Justice',
    label: 'Regulatory Evolution',
    image: poundImg,
    quote: 'The law must be stable, but it must not stand still.',
  },
  {
    author: 'Lawrence Lessig',
    title: 'Code as Law',
    label: 'Cyber Jurisprudence',
    image: lessigImg,
    quote:
      'Code is law. In cyberspace, the architecture of the network regulates behavior as powerfully as any statute.',
  },
  {
    author: 'Oliver Wendell Holmes Jr.',
    title: 'Living Law',
    label: 'Judicial Pragmatism',
    image: holmesImg,
    quote:
      'The life of the law has not been logic; it has been experience \u2014 the felt necessities of the time.',
  },
  {
    author: 'Steve Jobs',
    title: 'Future-Ready',
    label: 'Legal Innovation',
    image: jobsImg,
    quote: 'Innovation distinguishes between a leader and a follower.',
  },
  {
    author: 'Alan Turing',
    title: 'Machine Intelligence',
    label: 'Foundations of AI',
    image: turingImg,
    quote:
      'We can only see a short distance ahead, but we can see plenty there that needs to be done.',
  },
  {
    author: 'Ada Lovelace',
    title: 'Algorithmic Vision',
    label: 'Computing Pioneers',
    image: lovelaceImg,
    quote:
      'The Analytical Engine weaves algebraic patterns just as the Jacquard loom weaves flowers and leaves.',
  },
  {
    author: 'Vusi Thembekwayo',
    title: 'Borderless Markets',
    label: 'Digital Globalization',
    image: thembekwayoImg,
    quote:
      'As of 2024, the internet has since made the world flat.',
  },
];

const ROTATE_MS = 24 * 60 * 60 * 1000; // 24 hours
const FADE_MS = 800;

const StatsSection = () => {
  // Pick which entry to show based on days since epoch so it's stable
  // across reloads and rotates exactly once every 24 hours.
  const initialIndex = Math.floor(Date.now() / ROTATE_MS) % entries.length;
  const [index, setIndex] = useState(initialIndex);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const msIntoCurrent = Date.now() % ROTATE_MS;
    const msUntilNext = ROTATE_MS - msIntoCurrent;

    let interval: number | undefined;

    const advance = () => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % entries.length);
        setVisible(true);
      }, FADE_MS);
    };

    const timeout = window.setTimeout(() => {
      advance();
      interval = window.setInterval(advance, ROTATE_MS);
    }, msUntilNext);

    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, []);

  const entry = entries[index];

  return (
    <section className="bg-white py-20">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <div
          className={`transition-opacity duration-700 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={entry.image}
            alt={`Portrait of ${entry.author}`}
            loading="lazy"
            width={112}
            height={112}
            className="w-28 h-28 rounded-full object-cover mx-auto mb-6 border-2 border-primary/20 shadow-sm"
          />
          <blockquote
            className="text-gray-700 italic text-xl md:text-2xl leading-relaxed mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            "{entry.quote}"
          </blockquote>
          <p className="text-base text-gray-500 mb-6">{entry.author}</p>
          <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
            {entry.title}
          </div>
          <div className="text-gray-600 font-medium uppercase tracking-wide text-xs">
            {entry.label}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
